import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../components/ConfirmDialog';
import { AdminPageLayout, Section, Badge } from '../../components';
import {
  Listing,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  ViewAction,
  CreateAction,
  CopyAction
} from '../../components/listing/v2/ListingAction';
import {
  Timer,
  Activity,
  Dumbbell,
  Wind,
  Users,
  Heart,
  Target,
  Lock
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useTemplateState, TemplateStateProvider } from '../../contexts/TemplateStateContext';
import apiService from '../../services/api';

const ActivityTemplateManagementContent = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  // Use State Pattern for context-specific behavior
  const { state, clubId, branchId } = useTemplateState();

  const [systemTemplates, setSystemTemplates] = useState([]);
  const [clubActivities, setClubActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    search: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get paths from state
  const paths = state.getActivityNavigationPaths(branchId);

  const ACTIVITY_TYPES = [
    { value: 'warmup', label: 'Warmup' },
    { value: 'conditioning', label: 'Conditioning' },
    { value: 'cooldown', label: 'Cooldown' },
    { value: 'skill_building', label: 'Skill Building' },
    { value: 'scrimmage', label: 'Scrimmage' },
    { value: 'support', label: 'Support' }
  ];

  const loadTemplatesAndActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Debug logging
      console.log('[ActivityTemplateManagement] Loading templates with context:', {
        clubId,
        branchId,
        contextType: state.getScope()
      });

      // Validate branchId for branch/coach contexts
      if (state.getScope() === 'branch' && !branchId) {
        const errorMsg = 'Branch ID is required but not available. Please ensure you are logged in as a coach with a branch assignment.';
        console.error('[ActivityTemplateManagement]', errorMsg);
        setError(errorMsg);
        showError(errorMsg);
        setLoading(false);
        return;
      }

      const activeOnly = filters.status === 'active';
      const endpoint = state.getActivityTemplateEndpoint(clubId, branchId);

      const data = await apiService.get(`${endpoint}?active_only=${activeOnly}`);
      const allTemplates = data?.templates || [];

      // Use state pattern to separate templates
      const { systemTemplates: sysTemplates, customTemplates: customActivities } =
        state.separateTemplates(allTemplates);

      setSystemTemplates(sysTemplates);
      setClubActivities(customActivities);
    } catch (err) {
      setError(err.message);
      showError(`Error loading activity templates: ${err.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, state, clubId, branchId]);

  useEffect(() => {
    loadTemplatesAndActivities();
  }, [loadTemplatesAndActivities]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleCopyTemplate = async (template) => {
    try {
      const targetScope = state.getScope();
      const targetScopeId = branchId || clubId;
      const copyLabel = state.getCopyLabel();

      await apiService.post(`/api/clubs/${clubId}/activity-templates/${template.id}/copy`, {
        target_scope: targetScope,
        target_scope_id: targetScopeId,
        customizations: {}
      });

      showSuccess(`Activity template "${template.name}" copied to ${copyLabel.replace('Copy to ', '').toLowerCase()}.`);
      // Small delay to ensure Firestore has propagated the write
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadTemplatesAndActivities();
    } catch (err) {
      showError(`Error copying activity template: ${err.message}`);
    }
  };

  const handleDeleteActivity = async (activity) => {
    setIsDeleting(true);

    try {
      const endpoint = state.getActivityTemplateEndpoint(clubId, branchId);
      await apiService.delete(`${endpoint}/${activity.id}`);

      const scopeName = state.getScope();
      showSuccess(`${scopeName.charAt(0).toUpperCase() + scopeName.slice(1)} activity "${activity.name}" deleted successfully.`);

      setShowDeleteModal(null);
      await loadTemplatesAndActivities();
    } catch (err) {
      showError(`Error deleting: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const applyFilters = (items) => {
    return items.filter(item => {
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        if (!item.name.toLowerCase().includes(searchTerm) &&
          !(item.description && item.description.toLowerCase().includes(searchTerm))) {
          return false;
        }
      }
      if (filters.type !== 'all') {
        if (item.activity_type !== filters.type) return false;
      }
      return true;
    });
  };

  const filteredSystemTemplates = applyFilters(systemTemplates);
  const filteredClubActivities = applyFilters(clubActivities);

  const getActivityTypeLabel = (type) => {
    const activityType = ACTIVITY_TYPES.find(t => t.value === type);
    return activityType ? activityType.label : type || 'Uncategorized';
  };

  const getActivityIcon = (type) => {
    const icons = {
      warmup: <Wind size={32} />,
      conditioning: <Dumbbell size={32} />,
      cooldown: <Heart size={32} />,
      skill_building: <Target size={32} />,
      scrimmage: <Users size={32} />,
      support: <Activity size={32} />
    };
    return icons[type] || <Activity size={32} />;
  };

  const isAlreadyCopied = (templateId) => {
    return clubActivities.some(activity => activity.source_template_id === templateId);
  };

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ]
        },
        {
          name: 'type',
          label: 'Activity Type',
          options: [
            { value: 'all', label: 'All Types' },
            ...ACTIVITY_TYPES
          ]
        }
      ]}
      activeFilters={{ status: filters.status, type: filters.type }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ status: 'all', type: 'all', search: '' })}
    />
  );

  const renderActivityItem = (item, isSystemTemplate = false) => {
    const alreadyCopied = isSystemTemplate && isAlreadyCopied(item.id);
    const canEdit = state.canEdit(item);
    const canDelete = state.canDelete(item);
    const canCopy = state.canCopy(item);
    const copyLabel = state.getCopyLabel();

    return (
      <ListingItem
        key={item.id}
        title={item.name}
        subtitle={item.description}
        icon={getActivityIcon(item.activity_type)}
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Always show view action */}
            <ViewAction size="xs" onClick={() => navigate(paths.view(item.id))} title="View Activity" />

            {/* Show edit/delete for templates user can modify */}
            {canEdit && (
              <EditAction size="xs" onClick={() => navigate(paths.edit(item.id))} title="Edit Activity" />
            )}
            {canDelete && (
              <DeleteAction size="xs" onClick={() => setShowDeleteModal(item)} title="Delete Activity" />
            )}

            {/* Show copy action for system templates (if not already copied) */}
            {isSystemTemplate && canCopy && !alreadyCopied && (
              <CopyAction size="xs" label={copyLabel} onClick={() => handleCopyTemplate(item)} title={copyLabel} />
            )}
          </div>
        }
      >
        <div style={{ display: 'flex', gap: '2.5rem', marginTop: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.5, letterSpacing: '0.05em' }}>Activity Type</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 600 }}>
              {getActivityIcon(item.activity_type)} {getActivityTypeLabel(item.activity_type)}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.5, letterSpacing: '0.05em' }}>Default Duration</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 600 }}>
              <Timer size={14} style={{ opacity: 0.5 }} /> {item.default_duration_mins} minutes
            </div>
          </div>

          {item.skill_id && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.5, letterSpacing: '0.05em' }}>Linked Skill</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 600 }}>
                <Target size={14} style={{ opacity: 0.5 }} /> Skill-based
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto', alignSelf: 'flex-end' }}>
            {isSystemTemplate && (
              <Badge variant="outline" size="sm" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Lock size={12} /> SYSTEM TEMPLATE
              </Badge>
            )}
            {alreadyCopied && (
              <Badge variant="success" size="sm">COPIED</Badge>
            )}
            {!isSystemTemplate && (
              <Badge variant="outline" size="sm" style={{ textTransform: 'uppercase' }}>
                {item.activity_type?.replace('_', ' ')}
              </Badge>
            )}
            {item.is_active ? (
              <Badge variant="success" size="sm">ACTIVE</Badge>
            ) : (
              <Badge variant="warning" size="sm">INACTIVE</Badge>
            )}
          </div>
        </div>
      </ListingItem>
    );
  };

  // Get UI configuration from state
  const uiConfig = state.getActivityManagementUIConfig();
  const pageBackPath = state.getBackPath('activities', branchId).replace('/activities', '');

  return (
    <AdminPageLayout
      title="ACTIVITY TEMPLATE MANAGEMENT"
      subtitle={uiConfig.pageSubtitle}
      backPath={pageBackPath}
      maxWidth="1400px"
      primaryActions={
        uiConfig.primaryActionLabel ? <CreateAction onClick={() => navigate(paths.create)} label={uiConfig.primaryActionLabel} /> : null
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
        {/* Custom Activities - Show for club and branch admins */}
        {uiConfig.showCustomSection && (
          <Section title={uiConfig.customActivitiesTitle}>
            <Listing
              loading={loading}
              items={filteredClubActivities}
              filters={filterBar}
              variant="list"
              renderItem={(activity) => renderActivityItem(activity, false)}
              emptyMessage="No custom activities created yet. Copy from system templates or create from scratch."
            />
          </Section>
        )}

        {/* System Templates */}
        {uiConfig.showSystemTemplatesSection && (
          <Section title={uiConfig.systemTemplatesTitle}>
            <Listing
              loading={loading}
              items={filteredSystemTemplates}
              filters={filterBar}
              variant="list"
              renderItem={(template) => renderActivityItem(template, true)}
              emptyMessage="No system templates available"
            />
          </Section>
        )}
      </div>

      {showDeleteModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowDeleteModal(null)}
          onConfirm={() => handleDeleteActivity(showDeleteModal)}
          title="Delete Activity Template"
          variant="danger"
          confirmText="Confirm Deletion"
          loading={isDeleting}
        >
          <p>
            Confirm deletion of the <strong>{showDeleteModal.name}</strong> activity template.
          </p>
          <p style={{ marginTop: '0.5rem', color: 'var(--color-error)', fontWeight: 600, fontSize: '0.9rem' }}>
            Warning: This activity template will be permanently deleted. Existing training sessions using this activity will not be affected.
          </p>
        </ConfirmDialog>
      )}
    </AdminPageLayout>
  );
};

// Wrap with provider
const ActivityTemplateManagement = () => (
  <TemplateStateProvider>
    <ActivityTemplateManagementContent />
  </TemplateStateProvider>
);

export default ActivityTemplateManagement;
